// Content for Śrīmad-Bhāgavatam 1.6.35 - The Three Grades of Bhakti

import type { TopicContent } from '../types';

const content: TopicContent = {
    primarySloka: {
        reference: 'Śrīmad-Bhāgavatam 1.6.35',
        sloka: `yamādibhir yoga-pathaiḥ
kāma-lobha-hato muhuḥ
mukunda-sevayā yadvat
tathātmāddhā na śāmyati`,
        translation:
            'The ātmā (<strong>ātmā</strong>) constantly afflicted (<strong>muhuḥ</strong>) by lust and greed (<strong>kāma-lobha-hataḥ</strong>) will not be satisfied (<strong>na śāmyati addhā</strong>) by aṣṭaṅga-yoga and other paths (<strong>yama-ādibhiḥ yoga-pathaiḥ</strong>) as much as by direct service to Mukunda (<strong>mukunda-sevayā yadvat tathā</strong>).',
    },
    blocks: [
        {
            type: 'text',
            content:
                'Though it is ascertained that the state of <em>bhakti</em> is real liberation, <em>yoga</em> and <em>jñāna</em> mixed with <em>bhakti</em> will not pacify the <em>ātmā</em> as much as pure <em>bhakti</em>. <em>Ātmā</em> is not pacified by <em>yoga</em> (<em>yoga-pathaiḥ</em>) as much as by serving Mukunda directly (<em>addhā</em>). It is established that <em>yoga</em> and other processes without <em>bhakti</em> are futile:',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Śrīmad-Bhāgavatam 10.14.5',
                sloka: `pureha bhūman bahavo 'pi yoginas
tvad-arpitehā nija-karma-labdhayā
vibudhya bhaktyaiva kathopanītayā
prapedire 'ñjo 'cyuta te gatiṁ parām`,
                translation:
                    'O almighty Lord, in the past many yogīs in this world achieved the platform of devotional service by offering all their endeavors unto You and faithfully carrying out their prescribed duties. Through such devotional service, perfected by the processes of hearing and chanting about You, they came to understand You, O infallible one, and could easily surrender to You and achieve Your supreme abode.',
            },
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Śrīmad-Bhāgavatam 1.5.12',
                sloka: `naiṣkarmyam apy acyuta-bhāva-varjitaṁ
na śobhate jñānam alaṁ nirañjanam
kutaḥ punaḥ śaśvad abhadram īśvara
na cārpitaṁ karma yad apy akāraṇam`,
                translation:
                    'Even the stage of jñāna without the bondage of karma is not glorious if it is devoid of bhakti to the Supreme Lord. What is the use of having destroyed ignorance? What then to speak of sakāma-karma which causes suffering, both, during practice and at the stage perfection, and niṣkāma-karma, when not offered to the Lord?',
            },
        },
        {
            type: 'text',
            content:
                'Therefore this verse must only refer to <em>yoga</em> and other processes mixed with <em>bhakti</em>, since they otherwise would give no results at all. And even if these processes pacify the <em>ātmā</em> to some extent, they do not pacify it to the extent that pure <em>bhakti</em> alone - serving Mukunda without these processes - does. These mixed processes will not satisfy <em>ātmā</em> completely:',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Śrīmad-Bhāgavatam 1.5.8',
                sloka: `bhavatānudita-prāyaṁ
yaśo bhagavato 'malam
yenaivāsau na tuṣyeta
manye tad darśanaṁ khilam`,
                translation:
                    'You have not sufficiently described the glories of the spotless Lord Kṛṣṇa. Because your mind could never be satisfied with Vedānta, I think that writing the Vedānta-sūtras is insufficient.',
            },
        },
        {
            type: 'text',
            content:
                'Though <em>bhakti</em> is described later as a means of liberation, three types of <em>bhakti</em> are seen: <strong>pure <em>bhakti</em> (<em>kevala</em>)</strong>, <strong>mixed <em>bhakti</em> but with <em>bhakti</em> being predominant (<em>prādhānya</em>)</strong> and <strong>mixed <em>bhakti</em> but with <em>bhakti</em> being secondary (<em>guṇa-bhāva</em>)</strong>. <strong><em>Kevala-bhakti</em></strong> is illustrated in verses such as <em>tyaktvā sva-dharmam</em> (SB 1.5.17) and <em>ahaṁ purātīta-bhava</em> (SB 1.5.23). <strong><em>Prādhānya-bhakti</em></strong> is illustrated in the following:',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Śrīmad-Bhāgavatam 1.5.36',
                sloka: `kurvāṇā yatra karmāṇi
bhagavac-chikṣayāsakṛt
gṛṇanti guṇa-nāmāni
kṛṣṇasyānusmaranti ca`,
                translation:
                    'When those engaged in karma continuously perform those activities according to the instruction of the Lord, they also chant and remember the qualities and names of Kṛṣṇa.',
            },
        },
        {
            type: 'text',
            content: '<strong><em>Guṇa-bhāva-bhakti</em></strong> is illustrated as follows:',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Śrīmad-Bhāgavatam 1.5.35',
                sloka: `yad atra kriyate karma
bhagavat-paritoṣaṇam
jñānaṁ yat tad adhīnaṁ hi
bhakti-yoga-samanvitam`,
                translation:
                    'That jñāna which arises from karma which is pleasing to the Lord because of being offered to him is endowed with bhakti.',
            },
        },
        {
            type: 'text',
            content:
                '<strong>1. Kevala-bhakti:</strong> Practiced by a person who is <em>niṣkāma</em>, also called as <em>ananya-bhakti</em>, <em>śuddha-bhakti</em>, <em>nirguṇa-bhakti</em>, <em>uttama-bhakti</em> and <em>akiñcana-bhakti</em>, <strong>gives <em>prema</em> as a result.</strong><br /><br /><strong>2. Prādhānya-bhakti:</strong> Classified as <em>karma-miśra-bhakti</em>, <em>jñāna-miśra-bhakti</em>, and <em>yoga-miśra-bhakti</em> and practiced by those who are <em>śānta</em>, <strong>produces <em>rati</em> (<em>bhāva</em>) and liberation for some.</strong> If one of these persons gets the association of a person with <em>dāsya-bhāva</em> or other sentiments, because of the predominance of <em>bhakti</em> desiring <em>dāsya</em> or other sentiments, that person will achieve <em>prema</em> from that <em>dāsya</em> or other <em>bhāva</em> with a prominence of reverential (<em>aiśvarya</em>) mood.<br /><br /><strong>3. Guṇa-bhāva-bhakti:</strong> <em>Bhakti</em> does not reveal its own results and name. It is only an assistant of <em>karma</em>, <em>jñāna</em> and <em>yoga</em> which cannot produce results without <em>bhakti</em>’s presence. <em>Bhakti</em> in this case is only secondary (<em>taṭastha</em>). <strong><em>Bhakti-miśra-karma</em>, <em>bhakti-miśra-jñāna</em> and <em>bhakti-miśra-yoga</em> produce liberation.</strong><br /><br /><strong>Conclusion:</strong> Thus in this scripture, <strong>only two types of <em>bhakti</em> are accepted: <em>kevala</em> and <em>pradhānībhūtā</em>.</strong> All this Nārada instructed to Vyāsa, who explains this in the Twelfth Canto.',
        },
    ],
};

export default content;